import { useState, useRef } from 'react';
import { useCaptureOverlay } from '../hooks/useCaptureOverlay';

// Re-export for backwards compatibility with existing code
export interface UploadedFile {
  id: string;
  name: string;
  type: 'photo' | 'document';
  dataUrl: string; // Base64 data URL for preview
  file: File;
  timestamp: Date;
}

// Specific type for photos - always 'photo'
export interface UploadedPhoto {
  id: string;
  name: string;
  type: 'photo'; // Always 'photo' for this component
  dataUrl: string;
  file: File;
  timestamp: Date;
}

interface PhotoUploadProps {
  uploads: UploadedPhoto[];
  onUpload: (file: UploadedPhoto) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
  maxUploads?: number;
  title?: string;
  description?: string;
  // Re-read a queued file's blob from IndexedDB and mint a fresh URL. Supplied
  // by useVisitTask so a thumbnail can self-heal after iOS evicts the in-memory
  // blob backing (#2127, diag #54). Optional: callers that render non-queued
  // files (no durable copy) simply omit it.
  recoverBlobUrl?: (uploadId: string) => Promise<string | null>;
}

function generateId(): string {
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// Self-heal a dead preview (#2127, diag #52 + #54). A blob: URL can die two ways:
//   stage 0 -> 1: revoked by our own lifecycle churn but the backing is alive,
//                 so re-minting from the in-memory File fixes it instantly.
//   stage 1 -> 2: the re-mint ALSO fails => iOS evicted the blob backing under
//                 memory pressure (the File shares that dead backing), so the
//                 only durable source is the persisted copy in visit_file_queue.
// onLoadReset clears the stage on every successful paint so a later eviction
// starts the recovery ladder fresh instead of short-circuiting to "broken".
export function makeImageErrorHandler(
  file: File,
  uploadId: string,
  recoverBlobUrl?: (uploadId: string) => Promise<string | null>
) {
  return (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const stage = img.dataset.recoverStage ?? '0';
    if (stage === '0') {
      img.dataset.recoverStage = '1';
      img.src = URL.createObjectURL(file);
      return;
    }
    if (stage === '1' && recoverBlobUrl) {
      img.dataset.recoverStage = '2';
      void recoverBlobUrl(uploadId).then((url) => {
        if (url) img.src = url;
      });
      return;
    }
    // stage 2 (or no durable source): truly gone — let the broken-image/alt show.
  };
}

export function resetRecoverStage(e: React.SyntheticEvent<HTMLImageElement>) {
  e.currentTarget.dataset.recoverStage = '0';
}

export default function PhotoUpload({
  uploads,
  onUpload,
  onRemove,
  disabled = false,
  maxUploads = 10,
  title = 'Photos',
  description = 'Take photos of shelf displays and store conditions',
  recoverBlobUrl,
}: PhotoUploadProps) {
  const [isCapturing, setIsCapturing] = useState(false);
  // Cancelling the camera fires no change event, so this is what un-sticks
  // the overlay when the rep backs out to pick an existing photo (#3448).
  useCaptureOverlay(isCapturing, () => setIsCapturing(false));
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<UploadedPhoto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (uploads.length >= maxUploads) {
        alert(`Maximum ${maxUploads} photos allowed`);
        break;
      }

      // Only accept images for photos
      if (!file.type.startsWith('image/')) {
        alert('Only image files are allowed for photos. Use the Documents section for other files.');
        continue;
      }

      const dataUrl = await readFileAsDataUrl(file);

      // Always type as 'photo' - user chose this section explicitly
      const uploadedFile: UploadedPhoto = {
        id: generateId(),
        name: file.name,
        type: 'photo',
        dataUrl,
        file,
        timestamp: new Date(),
      };

      onUpload(uploadedFile);
    }

    // Reset the input
    event.target.value = '';
    setIsCapturing(false);
  };

  const readFileAsDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCameraCapture = () => {
    setIsCapturing(true);
    cameraInputRef.current?.click();
  };

  const handleGallerySelect = () => {
    fileInputRef.current?.click();
  };

  const handlePreview = (file: UploadedPhoto) => {
    setPreviewFile(file);
    setPreviewUrl(file.dataUrl);
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setPreviewFile(null);
  };

  const canAddMore = uploads.length < maxUploads && !disabled;

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
          {title}
        </h3>
        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
          {uploads.length}/{maxUploads}
        </span>
      </div>

      {/* Description */}
      <p className="mb-3 text-xs text-slate-400">{description}</p>

      {/* Upload Buttons */}
      {canAddMore && (
        <div className="mb-4 flex gap-2">
          <button
            onClick={handleCameraCapture}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-3 text-sm font-medium text-slate-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Camera</span>
          </button>
          <button
            onClick={handleGallerySelect}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-3 text-sm font-medium text-slate-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Gallery</span>
          </button>
        </div>
      )}

      {/* Hidden file inputs - only accept images */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Uploaded Files Grid */}
      {uploads.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {uploads.map((upload) => (
            <div
              key={upload.id}
              className="group relative aspect-square overflow-hidden rounded-lg bg-slate-100"
            >
              <img
                src={upload.dataUrl}
                alt={upload.name}
                className="h-full w-full object-cover cursor-pointer"
                onClick={() => handlePreview(upload)}
                onLoad={resetRecoverStage}
                onError={makeImageErrorHandler(upload.file, upload.id, recoverBlobUrl)}
              />

              {/* Remove button */}
              {!disabled && (
                <button
                  onClick={() => onRemove(upload.id)}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {/* Type indicator */}
              <div className="absolute bottom-1 left-1 rounded bg-primary-600 px-1.5 py-0.5 text-[10px] text-white">
                IMG
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {uploads.length === 0 && !canAddMore && (
        <div className="py-6 text-center text-sm text-slate-400">
          No uploads allowed
        </div>
      )}

      {uploads.length === 0 && canAddMore && (
        <div className="py-4 text-center text-sm text-slate-400">
          {description}
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={closePreview}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]">
            <img
              src={previewUrl}
              alt={previewFile.name}
              className="max-h-[80vh] rounded-lg object-contain"
              onLoad={resetRecoverStage}
              onError={makeImageErrorHandler(previewFile.file, previewFile.id, recoverBlobUrl)}
            />
            <button
              onClick={closePreview}
              className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-lg"
            >
              <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="mt-4 text-center text-sm text-white">
              {previewFile.timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </div>
          </div>
        </div>
      )}

      {/* Capturing indicator */}
      {isCapturing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50">
          <div className="rounded-lg bg-white p-4 text-center">
            <div className="h-8 w-8 mx-auto animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
            <p className="mt-2 text-sm text-slate-600">Opening camera...</p>
          </div>
        </div>
      )}
    </div>
  );
}
